/**
 * **Die Schreibfelder einer Aufgabe als eine Kette** — Titel, Notiz und die Zeilen
 * der Checkliste. ↑ und ↓ gehen darin ein Glied weiter (Nutzer-Vorgabe,
 * 2026-09-19), damit man zum Wechseln nicht zur Maus greifen muss.
 *
 * **An beiden Orten dieselbe Kette** (Nutzer-Vorgabe, 2026-09-20): in den
 * **Aufgaben-Details** und in der aufgeklappten Karte der **Aufgabenliste**. Die
 * Klassen unterscheiden sich, der Weg nicht — deshalb liegt das hier in `utils/`
 * und nicht mehr bei den Details.
 *
 * **Am Baum abgelesen, nicht aus Refs zusammengesteckt.** Die Zeilen der Checkliste
 * kommen und gehen — vier Refs an vier Orten liefen bei der nächsten Zeile
 * auseinander. Die Reihenfolge des Baums IST die Reihenfolge auf dem Bild: Der Titel
 * steht oben, darunter die Notiz, daneben oder darunter die Unteraufgaben.
 */

/** Rückwärts (↑) oder vorwärts (↓) in der Kette. */
export type FieldStep = -1 | 1

/**
 * `input` vor der Klasse: Bei mehreren Ausgewählten steht an derselben Stelle ein
 * `span` mit „3 Aufgaben" — kein Feld, und nichts, worauf man springen könnte.
 */
const FIELD_SELECTOR = [
  // Aufgaben-Details
  'input.task-details__head-label',
  '.details-header__notes textarea',
  /*
    Aufgeklappte Karte in der Liste. **Der Titel ist dort eine `textarea`** und
    kein `input` — er wächst mit dem Text (`TaskEntry`). Für `isAtEdge` heißt
    das: Er lässt die Taste erst an seiner Kante los, und weil Enter dort
    speichert, hat er ohnehin nie einen zweiten Absatz.
  */
  'textarea.task-entry__title-input',
  'textarea.task-entry__notes',
  // Beiden gemeinsam
  '.subtask-row__title',
].join(', ')

type Field = HTMLInputElement | HTMLTextAreaElement

export function isField(node: EventTarget | null): node is Field {
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
}

/** Steht vor des Carets kein Absatz mehr, ist sie in der ersten Zeile. */
export function isAtFirstLine(value: string, selectionStart: number): boolean {
  return !value.slice(0, selectionStart).includes('\n')
}

/** Und hinter ihr keiner mehr, ist sie in der letzten. */
export function isAtLastLine(value: string, selectionEnd: number): boolean {
  return !value.slice(selectionEnd).includes('\n')
}

/**
 * Ob der Anschlag das Feld verlassen darf.
 *
 * Einzeilige Felder immer — dort gibt es kein Oben und Unten. In der Notiz erst an
 * ihrer Kante, sonst käme man mit den Pfeiltasten nicht mehr durch drei Absätze.
 *
 * Gemessen wird an harten Absätzen, nicht am Umbruch im Bild: Ein Absatz, der über
 * zwei Bildzeilen läuft, gilt als eine. Die genaue Antwort hieße, der Caret
 * nach dem Anschlag nachzumessen — ein Bild Verzögerung, und in jsdom nicht greifbar.
 */
export function isAtEdge(field: Field, step: FieldStep): boolean {
  if (field.tagName !== 'TEXTAREA') return true
  return step === -1
    ? isAtFirstLine(field.value, field.selectionStart ?? 0)
    : isAtLastLine(field.value, field.selectionEnd ?? field.value.length)
}

/** Alle Schreibfelder der Karte, in der Reihenfolge, in der sie dastehen. */
export function fieldChain(root: HTMLElement | null): Field[] {
  if (!root) return []
  return [...root.querySelectorAll<HTMLElement>(FIELD_SELECTOR)].filter(isField)
}

/**
 * Setzt den Caret ein Glied weiter. Gibt zurück, ob das gelungen ist — am
 * Anfang und am Ende der Kette ist es das nicht, und die Taste gehört dann wieder
 * dem Feld.
 *
 * **Die Marke landet am ENDE des Zieltextes**, wie überall sonst in den
 * Aufgaben-Details (`handleJump`, `SubtaskList.focusPrevious`): Man will anhängen,
 * nicht ersetzen.
 */
export function stepField(root: HTMLElement | null, from: Field, step: FieldStep): boolean {
  const fields = fieldChain(root)
  const index = fields.indexOf(from)
  if (index === -1) return false
  const next = fields[index + step]
  if (!next) return false
  next.focus()
  next.setSelectionRange(next.value.length, next.value.length)
  return true
}

/** `-1` für ↑, `1` für ↓, `null` für alles andere. */
export function stepForKey(key: string): FieldStep | null {
  if (key === 'ArrowUp') return -1
  if (key === 'ArrowDown') return 1
  return null
}

/** `-1` für ←, `1` für →, `null` für alles andere. */
export function sideStepForKey(key: string): FieldStep | null {
  if (key === 'ArrowLeft') return -1
  if (key === 'ArrowRight') return 1
  return null
}

/**
 * Stehen Notiz und Unteraufgaben nebeneinander?
 *
 * **Nur in den Aufgaben-Details gefragt**: Das seitliche Wechseln mit ← und →
 * hängt an deren Klassen. Die aufgeklappte Karte kennt bisher nur ↑ und ↓.
 *
 * Gefragt wird das LAYOUT, nicht die Geometrie: Die beiden Modifikatoren sind die
 * Quelle des Umbruchs (`DetailsHeader.scss`) — eine gefüllte Notiz und eine Liste mit
 * Einträgen nehmen je die ganze Zeile. Gemessene Kanten wären hier trügerisch: In
 * jsdom und in einem Tab hinter `display: none` liegt alles auf 0 und sähe damit immer
 * nebeneinander aus (CLAUDE.md, TabStack-Falle).
 */
const ROW_BREAKERS = '.details-header__notes--paragraph, .details-header__subtasks--list'

export function notesAndSubtasksShareRow(root: HTMLElement | null): boolean {
  if (!root) return false
  if (!root.querySelector('.details-header__notes')) return false
  return !root.querySelector(ROW_BREAKERS)
}

/** Ob der Caret am Rand des Textes steht — links am Anfang, rechts am Ende. */
export function isAtTextEdge(field: Field, step: FieldStep): boolean {
  const { selectionStart, selectionEnd } = field
  if (selectionStart === null || selectionEnd === null) return true
  if (selectionStart !== selectionEnd) return false
  return step === -1 ? selectionStart === 0 : selectionEnd === field.value.length
}

/**
 * **← und → wechseln zwischen Notiz und Unteraufgaben** (Nutzer-Vorgabe, 2026-09-19) —
 * aber nur, solange die beiden wirklich NEBENEINANDER stehen. Untereinander zeigte ein →
 * nach rechts und landete eine Zeile tiefer; das wäre eine Taste, die lügt.
 *
 * Die Marke läuft dabei WEITER in ihre Richtung — nach rechts an den Anfang des nächsten
 * Feldes, nach links an das Ende des vorigen. Anders als beim Sprung mit ↑ und ↓, wo man
 * anhängen will: Hier wandert man durch den Text, nicht zwischen Stationen.
 */
export function sideStepField(root: HTMLElement | null, from: Field, step: FieldStep): boolean {
  if (!notesAndSubtasksShareRow(root) || !root) return false

  const notes = root.querySelector<HTMLTextAreaElement>('.details-header__notes textarea')
  const subtask = root.querySelector<HTMLInputElement>('.subtask-row__title')
  if (!notes || !subtask) return false

  const target = from === notes && step === 1 ? subtask
    : from === subtask && step === -1 ? notes
      : null
  if (!target) return false

  target.focus()
  const caret = step === 1 ? 0 : target.value.length
  target.setSelectionRange(caret, caret)
  return true
}
